import React, { useEffect, useState, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import { useFirstVisit } from '../hooks/useFirstVisit';
import { useLang } from '../utils/i18n';

/**
 * A contextual tooltip that appears once per user, anchored to a target element.
 *
 * Renders via a Portal at the document body, so it is never clipped by ancestor
 * `overflow: hidden` or broken by ancestor CSS transforms (both of which make
 * `position: fixed` behave like `position: absolute`).
 *
 * All animations respect `prefers-reduced-motion`.
 */

interface CoachMarkProps {
  /** Unique localStorage key — once dismissed, the mark never shows again. */
  id: string;
  /** The element to anchor to. If null (ref not yet attached), the mark waits. */
  targetRef: React.RefObject<HTMLElement>;
  /** The tooltip text. */
  message: string;
  /** Milliseconds after mount to wait before showing. Default 0. */
  delay?: number;
  /** Placement relative to the target. Default 'bottom'. */
  placement?: 'top' | 'bottom' | 'left' | 'right';
  /** Optional callback when dismissed. */
  onDismiss?: () => void;
}

const CoachMark: React.FC<CoachMarkProps> = ({
  id, targetRef, message, delay = 0, placement = 'bottom', onDismiss,
}) => {
  const { t } = useLang();
  const [isFirst, dismiss] = useFirstVisit(`npw_coach_${id}`);
  const [show, setShow] = useState(false);
  const [pos, setPos] = useState<{ top: number; left: number; width: number; height: number } | null>(null);
  const [targetVisible, setTargetVisible] = useState(false);
  const cardRef = useRef<HTMLDivElement>(null);

  // Delay before showing
  useEffect(() => {
    if (!isFirst) return;
    const timer = setTimeout(() => setShow(true), delay);
    return () => clearTimeout(timer);
  }, [isFirst, delay]);

  // Position tracking — updates on scroll/resize
  const updatePosition = useCallback(() => {
    const el = targetRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    setTargetVisible(
      rect.bottom > 0 &&
      rect.right > 0 &&
      rect.top < window.innerHeight &&
      rect.left < window.innerWidth,
    );
    setPos({ top: rect.top, left: rect.left, width: rect.width, height: rect.height });
  }, [targetRef]);

  useEffect(() => {
    if (!show) return;
    updatePosition();
    const onScroll = () => updatePosition();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
    };
  }, [show, updatePosition]);

  // Dismiss handlers
  const handleDismiss = useCallback(() => {
    dismiss();
    setShow(false);
    onDismiss?.();
  }, [dismiss, onDismiss]);

  useEffect(() => {
    if (!show) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') handleDismiss();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [show, handleDismiss]);

  // A target further down a mobile page should introduce itself when the user
  // reaches it, not render an unreachable tooltip below the viewport.
  if (!isFirst || !show || !pos || !targetVisible) return null;

  // Calculate the anchor point (where the card attaches to the target) based on placement.
  //
  // CLS: the anchor moves on every scroll/resize. Updating `top`/`left` on a
  // fixed element each time is reported by the browser as a layout shift (the
  // exact thing Search Console flags). `transform` changes are not counted, so
  // the wrapper stays pinned at top:0/left:0 and is moved with translate3d.
  // The card sits inside it, absolutely positioned relative to the anchor.
  const GAP = 12;
  let anchorX: number;
  let anchorY: number;
  const cardStyle: React.CSSProperties = {
    position: 'absolute',
    width: 'max-content',
    // Keep card within viewport
    maxWidth: 'min(320px, calc(100vw - 32px))',
  };
  switch (placement) {
    case 'top':
      anchorX = pos.left + pos.width / 2;
      anchorY = pos.top - GAP;
      cardStyle.bottom = 0;
      cardStyle.left = 0;
      cardStyle.transform = 'translateX(-50%)';
      break;
    case 'left':
      anchorX = pos.left - GAP;
      anchorY = pos.top + pos.height / 2;
      cardStyle.top = 0;
      cardStyle.right = 0;
      cardStyle.transform = 'translateY(-50%)';
      break;
    case 'right':
      anchorX = pos.left + pos.width + GAP;
      anchorY = pos.top + pos.height / 2;
      cardStyle.top = 0;
      cardStyle.left = 0;
      cardStyle.transform = 'translateY(-50%)';
      break;
    case 'bottom':
    default:
      anchorX = pos.left + pos.width / 2;
      anchorY = pos.top + pos.height + GAP;
      cardStyle.top = 0;
      cardStyle.left = 0;
      cardStyle.transform = 'translateX(-50%)';
      break;
  }

  // Top/bottom cards are centred on their target. Near either viewport edge
  // that can put the message — including its dismiss controls — off-screen,
  // especially when an RTL entries panel anchors the Weights button on the
  // opposite side. Clamp the anchor using the card's maximum possible width.
  if ((placement === 'top' || placement === 'bottom') && typeof window !== 'undefined') {
    const viewportMargin = 16;
    const cardWidth = Math.min(320, Math.max(0, window.innerWidth - viewportMargin * 2));
    const halfCard = cardWidth / 2;
    anchorX = Math.max(
      viewportMargin + halfCard,
      Math.min(window.innerWidth - viewportMargin - halfCard, anchorX),
    );
  }

  const content = (
    <>
      {/* Pulsing highlight ring around the target */}
      <span
        aria-hidden="true"
        className="coach-pulse-ring"
        style={{
          position: 'fixed',
          zIndex: 59,
          top: 0,
          left: 0,
          transform: `translate3d(${pos.left - 4}px, ${pos.top - 4}px, 0)`,
          width: pos.width + 8,
          height: pos.height + 8,
          borderRadius: 12,
          pointerEvents: 'none',
        }}
      />

      {/* Zero-size fixed anchor, moved with transform (see CLS note above) */}
      <div
        style={{
          position: 'fixed',
          zIndex: 60,
          top: 0,
          left: 0,
          width: 0,
          height: 0,
          transform: `translate3d(${anchorX}px, ${anchorY}px, 0)`,
        }}
      >
        {/* Tooltip card */}
        <div
          ref={cardRef}
          role="tooltip"
          className="coach-card-enter"
          style={cardStyle}
          onClick={e => e.stopPropagation()}
        >
          <div className="bg-gray-900 dark:bg-gray-700 text-white rounded-xl shadow-2xl px-4 py-3 flex items-start gap-3 border border-gray-700 dark:border-gray-500">
            <p className="text-xs leading-relaxed flex-1 min-w-0">{message}</p>
            <button
              onClick={handleDismiss}
              className="flex-shrink-0 flex items-center gap-1 px-2 py-1 text-[11px] font-bold text-violet-300 hover:text-white hover:bg-white/10 rounded-lg transition-colors"
            >
              {t('entries.gotIt')}
            </button>
            <button
              onClick={handleDismiss}
              aria-label={t('entries.undoDismiss')}
              className="flex-shrink-0 p-0.5 text-gray-400 hover:text-white transition-colors"
            >
              <X size={12} />
            </button>
          </div>
          {/* Arrow pointing toward the target */}
          <span
            aria-hidden="true"
            className={`coach-arrow coach-arrow-${placement}`}
          />
        </div>
      </div>
    </>
  );


  // Portal to document.body so the tooltip escapes any ancestor overflow/transform
  if (typeof document === 'undefined') return null;
  return createPortal(content, document.body);
};

export default CoachMark;
